import { Formik, Form, FieldArray } from 'formik'
import * as Yup from 'yup'
import React from 'react'
import { FaCheckDouble, FaPlus, FaTrash } from 'react-icons/fa'
import { useTranslation } from 'react-i18next'

import CustomButton from '../../../../shared/CustomButton'
import DatePicker from '../../../../shared/DatePicker'
import DropdownField from '../../../../shared/DropdownField'
import TextInput from '../../../../shared/TextInput'
import CheckPermission from '../../../../hooks/CheckPermission'

const emptyItem = {
  item_name_en: '',
  item_name_ar: '',
  item_code: '',
  vat: false,
  quantity: 1,
  unit_price: 0,
  discount: 0,
  total_vat: 0
}

const PayerCreditInvoiceForm = ({ handleSubmit, loading, payers }) => {
  const { hasPermission } = CheckPermission()
  const { t } = useTranslation()

  const initialValues = {
    payer_id: '',
    date: new Date(),
    reason: '',
    invoice_details: [{ ...emptyItem }]
  }

  const validationSchema = Yup.object().shape({
    payer_id: Yup.string().trim().required(t('Payer is required')),
    date: Yup.date().required(t('Date is required')),
    reason: Yup.string().trim().required(t('Reason is required')),
    invoice_details: Yup.array()
      .of(
        Yup.object().shape({
          item_name_en: Yup.string().required(t('Item name is required')),
          item_name_ar: Yup.string().required(t('Arabic name is required')),
          item_code: Yup.string().required(t('Item code is required')),
          unit_price: Yup.number()
            .min(0, t('Price cannot be negative'))
            .required(t('Price is required')),
          quantity: Yup.number()
            .min(1, t('Quantity must be at least 1'))
            .required(t('Quantity is required')),
          discount: Yup.number()
            .min(0, t('Discount cannot be negative'))
            .required(t('Discount is required')),
          total_vat: Yup.number().when('vat', {
            is: true,
            then: schema =>
              schema
                .min(0, t('VAT cannot be negative'))
                .required(t('VAT is required')),
            otherwise: schema => schema.notRequired()
          })
        })
      )
      .min(1, t('At least one item is required'))
  })

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={values => {
        if (!hasPermission('billing', 'create')) return
        handleSubmit(values)
      }}
    >
      {({
        errors,
        touched,
        handleChange,
        handleBlur,
        values,
        setFieldValue
      }) => {
        const calculateVat = item => {
          const unitPrice = parseFloat(item.unit_price) || 0
          const discount = parseFloat(item.discount) || 0
          const quantity = parseFloat(item.quantity) || 0
          const taxableAmount = Math.max(0, unitPrice - discount)
          return (taxableAmount * quantity * 0.15).toFixed(2)
        }

        const itemsTotal =
          values.invoice_details?.reduce((acc, item) => {
            const net =
              (Number(item.unit_price) - Number(item.discount)) *
              Number(item.quantity)
            const vat = item.vat ? Number(item.total_vat) : 0
            return acc + (net > 0 ? net : 0) + vat
          }, 0) || 0

        return (
          <Form>
            <div className='grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-3'>
              <DropdownField
                name='payer_id'
                label={t('Payer')}
                placeholder={t('Select payer')}
                options={payers}
                value={values.payer_id}
                onChange={e => setFieldValue('payer_id', e.value)}
                useFormik={false}
                filter
              />
              <DatePicker
                name='date'
                label={t('Date')}
                placeholder={t('Date')}
                value={values.date}
                onChange={e => setFieldValue('date', e.value)}
                useFormik={false}
              />
            </div>
            {errors.payer_id && touched.payer_id && (
              <span className='text-sm text-red-500 block -mt-2 mb-2'>
                {errors.payer_id}
              </span>
            )}
            {errors.date && touched.date && (
              <span className='text-sm text-red-500 block -mt-2 mb-2'>
                {errors.date}
              </span>
            )}

            <div className='flex flex-col gap-1 w-full mb-3'>
              <label className='input-label'>{t('Add Reason')}</label>
              <textarea
                placeholder={t('Enter reason')}
                className='w-full p-2 bg-gray-100 rounded-lg border border-gray-300 hover:border-primary !outline-none !shadow-none text-[14px]'
                name='reason'
                value={values.reason}
                onChange={handleChange}
                onBlur={handleBlur}
                rows={3}
              />
              {errors.reason && touched.reason && (
                <span className='text-sm text-red-500'>{errors.reason}</span>
              )}
            </div>

            <FieldArray name='invoice_details'>
              {({ push, remove }) => (
                <div className='my-2'>
                  <div className='flex justify-between items-center mb-2'>
                    <h2 className='text-lg font-semibold'>{t('Items')}</h2>
                    <CustomButton
                      icon={<FaPlus size={12} className='me-2' />}
                      label={t('Add Item')}
                      buttonType='primary'
                      onClick={() => push({ ...emptyItem })}
                    />
                  </div>
                  <div className='w-full border border-gray-200 rounded-lg overflow-auto max-h-[400px]'>
                    <table className='w-full min-w-[1100px]'>
                      <thead className='sticky top-0 z-10 bg-gray-50'>
                        <tr>
                          <th className='table-header !font-bold ps-4'>
                            {t('Name EN')}
                          </th>
                          <th className='table-header !font-bold'>
                            {t('Name AR')}
                          </th>
                          <th className='table-header !font-bold'>
                            {t('Code')}
                          </th>
                          <th className='table-header !font-bold'>
                            {t('Price')}
                          </th>
                          <th className='table-header !font-bold'>
                            {t('Qty')}
                          </th>
                          <th className='table-header !font-bold'>
                            {t('Disc')}
                          </th>
                          <th className='table-header !font-bold text-center'>
                            {t('VAT')}
                          </th>
                          <th className='table-header !font-bold'>
                            {t('VAT Amt')}
                          </th>
                          <th className='table-header !font-bold text-center'>
                            {t('Actions')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {values.invoice_details.map((item, index) => (
                          <tr
                            key={index}
                            className='bg-white border-b hover:bg-gray-50 transition-colors'
                          >
                            <td className='px-4 py-2'>
                              <TextInput
                                name={`invoice_details.${index}.item_name_en`}
                                placeholder={t('Name EN')}
                                parentClass='!mb-0'
                              />
                            </td>
                            <td className='px-4 py-2'>
                              <TextInput
                                name={`invoice_details.${index}.item_name_ar`}
                                placeholder={t('Name AR')}
                                parentClass='!mb-0'
                              />
                            </td>
                            <td className='px-4 py-2'>
                              <TextInput
                                name={`invoice_details.${index}.item_code`}
                                placeholder={t('Code')}
                                parentClass='!mb-0'
                              />
                            </td>
                            <td className='px-4 py-2'>
                              <TextInput
                                name={`invoice_details.${index}.unit_price`}
                                type='number'
                                placeholder={t('Price')}
                                parentClass='!mb-0'
                                className='!w-24'
                                step='any'
                                onChange={e => {
                                  handleChange(e)
                                  if (values.invoice_details[index].vat) {
                                    setFieldValue(
                                      `invoice_details.${index}.total_vat`,
                                      calculateVat({
                                        ...values.invoice_details[index],
                                        unit_price: e.target.value
                                      })
                                    )
                                  }
                                }}
                              />
                            </td>
                            <td className='px-4 py-2'>
                              <TextInput
                                name={`invoice_details.${index}.quantity`}
                                type='number'
                                placeholder={t('Qty')}
                                parentClass='!mb-0'
                                className='!w-24'
                                onChange={e => {
                                  handleChange(e)
                                  if (values.invoice_details[index].vat) {
                                    setFieldValue(
                                      `invoice_details.${index}.total_vat`,
                                      calculateVat({
                                        ...values.invoice_details[index],
                                        quantity: e.target.value
                                      })
                                    )
                                  }
                                }}
                              />
                            </td>
                            <td className='px-4 py-2'>
                              <TextInput
                                name={`invoice_details.${index}.discount`}
                                type='number'
                                placeholder={t('Disc')}
                                parentClass='!mb-0'
                                className='!w-24'
                                step='any'
                                onChange={e => {
                                  handleChange(e)
                                  if (values.invoice_details[index].vat) {
                                    setFieldValue(
                                      `invoice_details.${index}.total_vat`,
                                      calculateVat({
                                        ...values.invoice_details[index],
                                        discount: e.target.value
                                      })
                                    )
                                  }
                                }}
                              />
                            </td>
                            <td className='px-4 py-2 text-center'>
                              <input
                                type='checkbox'
                                name={`invoice_details.${index}.vat`}
                                checked={values.invoice_details[index].vat}
                                onChange={e => {
                                  handleChange(e)
                                  if (e.target.checked) {
                                    setFieldValue(
                                      `invoice_details.${index}.total_vat`,
                                      calculateVat(values.invoice_details[index])
                                    )
                                  } else {
                                    setFieldValue(
                                      `invoice_details.${index}.total_vat`,
                                      0
                                    )
                                  }
                                }}
                                className='text-primary bg-gray-100 border-gray-300 rounded focus:ring-primary h-[18px] w-[18px]'
                              />
                            </td>
                            <td className='px-4 py-2'>
                              {values.invoice_details[index].vat && (
                                <TextInput
                                  name={`invoice_details.${index}.total_vat`}
                                  type='number'
                                  placeholder={t('VAT')}
                                  parentClass='!mb-0'
                                  className='!w-24'
                                  step='any'
                                />
                              )}
                            </td>
                            <td className='px-4 py-2 text-center'>
                              <button
                                type='button'
                                onClick={() => remove(index)}
                                className='text-red-500 hover:text-red-700 transition-colors p-2 rounded-md hover:bg-red-50'
                                disabled={values.invoice_details.length === 1}
                                title={t('Remove Item')}
                              >
                                <FaTrash size={18} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </FieldArray>
            <div className='flex items-center justify-end w-full my-3'>
              <div className='bg-gray-100 p-2 rounded-md flex items-center justify-between gap-2 min-h-[40px] w-full max-w-[400px]'>
                <h1 className='font-semibold'>{t('Items Total')}:</h1>
                <span className='font-semibold text-primary'>
                  {itemsTotal.toFixed(2)}
                </span>
              </div>
            </div>

            {hasPermission('billing', 'create') && (
            <div className='flex justify-end'>
              <CustomButton
                loading={loading}
                type='submit'
                icon={<FaCheckDouble size={12} className='me-2' />}
                label={t('Create Credit Company Invoice')}
                buttonType='primary'
              />
            </div>
            )}
          </Form>
        )
      }}
    </Formik>
  )
}

export default PayerCreditInvoiceForm
